/**
 * 公共请求封装
 * - 除登录、注册外，请求头自动携带 token
 * - 401 自动清除登录态并跳转登录页
 */

import axios, { type InternalAxiosRequestConfig } from 'axios'

const AUTH_STORAGE_KEY = 'asset_auth'

/** 开发环境不设则走相对路径，由 Vite 代理转发到本地后端；测试/生产需配置完整 baseURL */
const baseURL = import.meta.env.VITE_API_BASE_URL ?? ''

/** 不需要携带 token 的路径（登录、注册等） */
const NO_AUTH_PATHS = ['/login', '/register', '/token', '/auth/login', '/auth/register', '/api/auth/login', '/api/auth/register']

function needAuth(url: string = ''): boolean {
  const path = url.replace(baseURL, '').split('?')[0]
  return !NO_AUTH_PATHS.some((p) => path.includes(p))
}

export function getToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as { token?: string }
    return data?.token ?? null
  } catch {
    return null
  }
}

export function clearAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY)
}

const request = axios.create({
  baseURL: baseURL || undefined,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (needAuth(config.url)) {
      const token = getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
         config.headers.token = `${token}`
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

request.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    if (error.response?.status === 401) {
      clearAuth()
      const path = window.location.pathname
      if (!path.startsWith('/login') && !path.startsWith('/register')) {
        window.location.href = '/login'
      }
    }
    const status = error.response?.status
    const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(String(error.message))
    const msg =
      error.response?.data?.detail ??
      error.response?.data?.message ??
      error.message ??
      '请求失败'
    // 给调用方用的统一文案（超时、500 等提示更友好）
    const friendlyMessage = isTimeout
      ? '请求超时，请检查网络或稍后重试'
      : status === 500
        ? '服务器错误，请稍后重试'
        : typeof msg === 'string'
          ? msg
          : '请求失败'
    ;(error as any).friendlyMessage = friendlyMessage
    if (status !== 401 && import.meta.env.DEV) {
      console.warn('[request]', status, friendlyMessage, error.response?.data)
    }
    return Promise.reject(error)
  }
)

export default request
